import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { superApi } from './SuperAdminAuth';
import { Gauge, RefreshCw, AlertTriangle } from 'lucide-react';

const SuperApiQuotasPage = () => {
  const [data, setData] = useState({ providers: [], usage: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await superApi.get('/super/api-quotas');
      setData(r.data);
    } catch {
      toast.error('Failed to load API quotas');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, []);

  // Group usage by provider
  const byProvider = useMemo(() => {
    const out = {};
    for (const u of data.usage || []) {
      if (!out[u.provider]) out[u.provider] = [];
      out[u.provider].push(u);
    }
    return out;
  }, [data]);

  if (loading) return <div className="p-8 text-slate-400">Loading API quotas…</div>;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="super-api-quotas">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Gauge className="w-6 h-6 text-amber-400" />
          <h1 className="text-2xl font-semibold text-white">API Quotas</h1>
        </div>
        <button
          onClick={() => load()}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Per-tenant API call counts against each provider's rate limit, within that provider's rolling window.
        Any integration can register usage via <code className="font-mono bg-slate-900 px-1.5 py-0.5 rounded">record_api_usage()</code>.
      </p>

      <div className="space-y-6">
        {(data.providers || []).map(p => {
          const rows = byProvider[p.name] || [];
          return (
            <section key={p.name} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-white capitalize">{p.name.replace('_', ' ')}</h2>
                  <p className="text-xs text-slate-500">{p.description}</p>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  limit: {p.limit.toLocaleString()} / {p.window_hours}h
                </div>
              </header>
              {rows.length === 0 ? (
                <div className="px-5 py-4 text-xs text-slate-500">No usage in the current window.</div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {rows.slice(0, 25).map((u, i) => (
                    <div key={`${u.company_id}-${i}`} className="px-5 py-2.5 flex items-center gap-4">
                      <div className="flex-1 text-sm text-slate-200">{u.company_name}</div>
                      <div className="flex-1 max-w-xs">
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              u.percent >= 80 ? 'bg-rose-400' :
                              u.percent >= 50 ? 'bg-amber-400' : 'bg-emerald-400'
                            }`}
                            style={{ width: `${Math.min(100, u.percent || 0)}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-24 text-right text-xs text-slate-400 font-mono">
                        {u.used.toLocaleString()} / {u.limit.toLocaleString()}
                      </div>
                      <div className={`w-12 text-right text-xs font-mono ${
                        u.percent >= 80 ? 'text-rose-400' : u.percent >= 50 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>
                        {u.percent}%
                      </div>
                      {u.percent >= 80 && <AlertTriangle className="w-3 h-3 text-rose-400" />}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <p className="text-xs text-slate-600 mt-6">
        Generated at {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'} · Auto-refresh every 60s
      </p>
    </div>
  );
};

export default SuperApiQuotasPage;
